import clsx from 'clsx'
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Typography,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import ArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import ArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import { Link } from '@src/components/Link'
import LoginPopover from '@src/components/LoginPopover'
import {
  aldrichMarketSelectRoute,
  documentLibraryRoute,
  homeRoute,
  InviteCoWorkerRoute,
  loginRoute,
  orderCenterRoutes,
  profileRoutes,
  quickOrderRoute,
  registerRoute,
  softwareLibraryRoutes,
  subscriptionsRoutes,
  useRouter,
} from '@src/routes'
import { useB2BPunchoutLogoffDataLazyQuery } from '@src/queries/B2BPuchoutLogoffQuery.generated'
import {
  sendHeaderAccountMenuEvents,
  sendHeaderMenuEvents,
  switchAccountTracking,
} from '@utils/analytics'
import { EventKeys } from '@utils/analytics/enums'
import InventorySearchModal from './InventorySearchModal'
import SwitchAccountModal from './SwitchAccountModal'
import { useCurrentUser } from '@utils/useCurrentUser'
import { useUserSession } from '@utils/useUserSession'
import React, { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import postB2bData from '@utils/b2bExternalRedirect'
import {
  SnackbarMessages,
  SnackbarType,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import { extractData } from '@utils/errorHandler'
import { sessionStorage } from 'react-storage'
import NewTag from '../NewTag'
import EmailVerificationModal from './EmailVerificationModal'
import { AppCookies, useCookies } from '@src/utils/cookies'
import { useShowInviteCoworker } from '@src/utils/useShowInviteCoworker'
import { MenuOption } from './HeaderUtils'
import {
  InviteCoWorkerWalkMeStep,
  useInviteCoWorkerWalkMe,
} from '@src/routes/InviteCoWorker/InviteCoWorkerWalkMe'
import { useGlobalAlertBar } from '@src/components/GlobalAlertBar/globalAlertBarContext'
import {
  InvoicesWalkMeStep,
  useInvoicesWalkMe,
  InvoicesWalkMeAlert,
} from '@src/routes/OrderCenter/Invoices/InvoicesWalkMe'
import getConfig from 'next/config'

const useAccountStyles = makeStyles((theme: Theme) => ({
  mainNavLinkContainer: {
    display: 'flex',
  },
  mainNavLink: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.common.white,
    textDecoration: 'none',
    position: 'relative',
    '&:hover': {
      color: theme.palette.common.white,
    },
  },
  mainNavLinkArrow: {
    fontSize: theme.typography.pxToRem(21),
    marginLeft: theme.spacing(1),
    marginRight: '-2px',
  },
  popoverContent: {
    minWidth: 247,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    boxShadow: '0 6px 6px 0 rgba(0, 0, 0, 0.3)',
    borderRadius: 0,
    position: 'absolute',
    zIndex: theme.zIndex.modal,
    backgroundColor: theme.palette.common.white,
  },
  popoverLink: {
    display: 'block',
    textDecoration: 'none',
    fontSize: theme.typography.pxToRem(16),
    padding: '8px 0 8px',
    lineHeight: 1,
    color: theme.palette.common.black,
    transition: 'all .2s',
    fontWeight: theme.typography.fontWeightRegular,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
    },
  },
  popoverLoginRegister: {
    padding: theme.spacing(8, 0),
  },
  popoverDivider: {
    width: '100%',
    marginTop: 25,
    marginBottom: 17,
  },
  popoverLoggedInDivider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  popoverRegisterText: {
    alignSelf: 'start',
    marginBottom: 5,
  },
  menuActive: {
    background: '#ffffff',
    color: 'black',
    height: '100%',
    borderRight: 'none',
    '& $mainNavLink': {
      color: 'black',
    },
    '& $userNameDisplay': {
      color: theme.palette.common.black,
    },
  },
  accountWelcome: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(16),
    maxWidth: 270,
    overflow: 'hidden',
    paddingTop: theme.spacing(7.5),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  loginLinkWrapper: {
    width: '100%',
  },
  signInButton: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  registerButton: {
    paddingTop: 7,
    paddingBottom: 7,
  },
  accountContainer: {
    position: 'relative',
    height: '100%',
  },
  linkPosition: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  userNameDisplay: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '118px',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(14),
    color: '#fff',
  },
  navItem: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    paddingRight: theme.spacing(4),
    color: theme.palette.common.white,
    '&:hover': {
      color: theme.palette.common.white,
    },
    '&:not(:first-child)': {
      paddingLeft: theme.spacing(4),
    },
    '&:not(:last-child)': {
      borderRight: `2px solid ${theme.palette.brand.light}`,
    },
  },
  navRightItem: {
    display: 'flex',
    flexShrink: 0,
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    borderRight: `2px solid ${theme.palette.brand.light}`,
    alignItems: 'center',
  },
  coworks: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  walkMe: {
    position: 'absolute',
    top: '34px',
    left: '115px',
  },
  iframe: {
    display: 'none',
  },
}))

interface AccountMenuProps {
  isAuthorized: boolean
}

const AccountMenu: React.FC<AccountMenuProps> = ({ isAuthorized }) => {
  const classesAccount = useAccountStyles()
  const router = useRouter()
  const { formatMessage } = useIntl()
  const { setSnackbar, setGlobalSnackbarState } = useGlobalSnackbar()
  const {
    isB2BUser,
    isInventoryViewUser,
    isSourcingUser,
    currentUser,
    userIsLoggedIn,
    isDigitalProductEnabled,
    isOrderStatusCustomer,
    isCustomerServiceRep,
    isExternalSalesAgent,
    isQuoteConverter,
    isQuoteEnabled,
    isQuoteRequester,
    hasUniqueSoldTos,
    isSwitchAccountsEnabled,
    isInvoicePaymentCustomer,
    userId,
  } = useCurrentUser()

  const canAccessQuotes =
    (isQuoteConverter || isQuoteRequester) && isQuoteEnabled

  const { userSessionActions } = useUserSession()
  const [
    getB2BPunchOutLogoffData,
    { data: b2bPunchOutLogoffData, error: b2bPunchOutLogoffError },
  ] = useB2BPunchoutLogoffDataLazyQuery()

  const { shouldShowInviteCoworker } = useShowInviteCoworker()

  const [inventorySearchOpen, setInventorySearchOpen] =
    React.useState<boolean>(false)
  const [menuOpen, setMenuOpen] = React.useState<boolean>(false)
  const [openSwitchAccount, setOpenSwitchAccount] =
    React.useState<boolean>(false)
  const [stepNumber, setStepNumber] = React.useState(1)

  const [isEmailVerified, setIsEmailVerified] = React.useState<boolean>(true)
  const step1AnchorEl = React.useRef<HTMLDivElement>(null)
  const step2AnchorEl = React.useRef<HTMLDivElement>(null)
  const { advanceActiveStepNumber, startTour } = useInviteCoWorkerWalkMe()
  const [cookies] = useCookies()
  const { shouldHideAlert } = useInviteCoWorkerWalkMe()
  const showWalkMeAlert = !shouldHideAlert

  const {
    shouldHideAlert: shouldHideInvoicesAlert,
    advanceActiveStepNumber: advanceInvoiceStepNumber,
    dismissAlert: dismissInvoicesAlert,
    closeAlert: closeInvoicesAlert,
    state,
  } = useInvoicesWalkMe()
  const step1InvoiceAnchorEl = React.useRef<HTMLDivElement>(null)
  const {
    publicRuntimeConfig: {
      featureFlags: { oidcAuthEnabled },
    },
  } = getConfig()
  const { setAlertBarReturnUrl } = useGlobalAlertBar()

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    setMenuOpen(true)
  }

  const handleClose = (menuItem?: string, linkUrl?: string) => {
    if (menuItem) {
      sendHeaderAccountMenuEvents(menuItem, linkUrl)
    }
    setMenuOpen(false)
  }

  const [switchAccountOptionVisible, setSwitchAccountOptionVisible] =
    React.useState<boolean>(false)

  const [hideVerifyEmailModal, setHideVerifyEmailModal] =
    React.useState<boolean>(true)
  const handleLogout = async (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    handleClose(MenuOption.Logout, '/logout')
    sessionStorage.setItem<boolean>(EventKeys.IS_WECHAT_LOGGED_IN, false)

    if (!isB2BUser) {
      await userSessionActions.logout(router)
    } else {
      await getB2BPunchOutLogoffData()
      //clear session storage created to check for B2B user login event
      sessionStorage.removeItem(EventKeys.IS_B2B_LOGGED_IN_CALLED)
    }
  }
  //handling the error if postUrl is empty or null
  //display error message and logout the user since he will be already logged out by the API call
  //navigate to quick order/ home
  const b2bLogoutErrorRedirect = async () => {
    const code = 'DATA_ERROR'
    const errorMessage = formatMessage({ id: code, defaultMessage: 'Error' })
    setSnackbar(SnackbarMessages.B2bLogoutError, errorMessage)
    await userSessionActions.b2bLogout()
    if (
      router.pathname.includes('/cart') ||
      router.pathname.includes('/order-center')
    ) {
      router.replace(quickOrderRoute.index())
    } else {
      router.replace(homeRoute.index())
    }
  }
  //b2b logut function
  const b2bLogout = async () => {
    await userSessionActions.b2bLogout()
    const punchoutResponseMsg =
      b2bPunchOutLogoffData?.getB2BPunchOutLogoffData?.punchoutResponseMsg || ''
    const postBackURL =
      b2bPunchOutLogoffData?.getB2BPunchOutLogoffData?.postBackURL || ''
    const isDecodeNeeded =
      b2bPunchOutLogoffData?.getB2BPunchOutLogoffData?.isDecodeNeeded || false
    const isSapOci =
      b2bPunchOutLogoffData?.getB2BPunchOutLogoffData?.isSapOci || false
    postB2bData(postBackURL, punchoutResponseMsg, isDecodeNeeded, isSapOci)
  }
  useEffect(() => {
    b2bPunchOutLogoffData?.getB2BPunchOutLogoffData?.punchoutResponseMsg &&
      b2bPunchOutLogoffData?.getB2BPunchOutLogoffData?.postBackURL &&
      b2bPunchOutLogoffData?.getB2BPunchOutLogoffData?.postBackURL !== '' &&
      b2bLogout()
    if (
      b2bPunchOutLogoffData &&
      b2bPunchOutLogoffData.getB2BPunchOutLogoffData &&
      (!b2bPunchOutLogoffData.getB2BPunchOutLogoffData.postBackURL ||
        b2bPunchOutLogoffData.getB2BPunchOutLogoffData.postBackURL === '')
    ) {
      b2bLogoutErrorRedirect()
    }
  }, [b2bPunchOutLogoffData])

  useEffect(() => {
    if (b2bPunchOutLogoffError) {
      const { displayableError } = extractData(b2bPunchOutLogoffError)
      setGlobalSnackbarState({
        open: true,
        message: displayableError || (
          <FormattedMessage
            id="GENERIC_ERROR"
            defaultMessage="Sorry, an unexpected error has occurred"
          />
        ),
        variant: SnackbarType.Error,
      })
    }
  }, [b2bPunchOutLogoffError])

  useEffect(() => {
    if (!userIsLoggedIn && router.pathname === '/[country]/[language]/cart') {
      setMenuOpen(true)
    }
    const emproveEmailVerified =
      currentUser?.participantCommunicationSetting?.emproveEmailVerification
    setIsEmailVerified(emproveEmailVerified ? true : false)
  }, [userIsLoggedIn, router.pathname])

  useEffect(() => {
    if (
      userIsLoggedIn &&
      router.pathname === '/[country]/[language]/cart' &&
      (router.query.action === 'select_addresses' ||
        router.query.action === 'select_account')
    ) {
      setSwitchAccountOptionVisible(false)
    } else {
      const partners: any[] =
        currentUser?.__typename === 'LoggedInUser' &&
        currentUser?.soldToPartners
          ? [...currentUser?.soldToPartners]
          : []

      const uniquePartners = [
        ...new Map(partners.map((item) => [item['soldTo'], item])).values(),
      ]
      uniquePartners &&
        uniquePartners.length > 1 &&
        setSwitchAccountOptionVisible(true)
    }
  }, [userIsLoggedIn, router])

  useEffect(() => {
    if (userIsLoggedIn && userId) {
      const hide =
        cookies && cookies[`${AppCookies.HideEmailVerifionModal}_${userId}`]

      setHideVerifyEmailModal(hide === 'true' ? true : false)
      if (isAuthorized) {
        startTour(2000)
      }
    }
  }, [userIsLoggedIn, userId])

  const id = menuOpen ? 'profile' : ''

  const logonId =
    currentUser && currentUser.__typename === 'LoggedInUser'
      ? currentUser.logonId
      : ''

  return (
    <>
      {!shouldHideInvoicesAlert &&
        !state.enabled &&
        isInvoicePaymentCustomer && (
          <InvoicesWalkMeAlert
            dismissAlert={dismissInvoicesAlert}
            closeAlert={closeInvoicesAlert}
            clickNext={() => {
              setMenuOpen(true)
              advanceInvoiceStepNumber(1)
            }}
          />
        )}

      <div className={classesAccount.accountContainer}>
        {shouldShowInviteCoworker && stepNumber === 1 && showWalkMeAlert && (
          <div className={classesAccount.walkMe}>
            <InviteCoWorkerWalkMeStep
              anchorEl={step1AnchorEl}
              placement="bottom"
              stepNumber={stepNumber}
              clickNext={(arg) => {
                setStepNumber(arg)
                advanceActiveStepNumber(2)
                arg === 2 && setMenuOpen(true)
              }}
            />{' '}
            <span>
              <Typography ref={step1AnchorEl} variant="inherit">
                {}
              </Typography>
            </span>
          </div>
        )}
        <div className={classesAccount.linkPosition}>
          <div
            className={clsx(
              classesAccount.navRightItem,
              classesAccount.navItem,
              classesAccount.mainNavLinkContainer,
              {
                [classesAccount.menuActive]: menuOpen,
              }
            )}
            data-testid="header-logged-in-menu"
            id="header-logged-in-menu"
          >
            {oidcAuthEnabled ? (
              <a
                href={`/oidc-sign-in?redirect=${encodeURIComponent(
                  router.asPath || homeRoute.index()
                )}`}
                //eslint-disable-next-line
                onClick={userIsLoggedIn ? handleClick : () => {}}
                className={classesAccount.mainNavLink}
                aria-expanded={menuOpen}
                aria-haspopup="true"
                aria-controls={id}
                role="button"
              >
                {userIsLoggedIn ? (
                  <>
                    <FormattedMessage
                      id="HELLO_USER"
                      defaultMessage="Hello, {firstName}"
                      values={{
                        firstName: currentUser?.firstName,
                      }}
                    >
                      {(text) => (
                        <Typography
                          component="p"
                          className={classesAccount.userNameDisplay}
                        >
                          {text}
                        </Typography>
                      )}
                    </FormattedMessage>
                  </>
                ) : (
                  <span>
                    <FormattedMessage id="LOGIN" defaultMessage="Login" /> /{' '}
                    <FormattedMessage id="REGISTER" defaultMessage="Register" />
                  </span>
                )}
                {!userIsLoggedIn ? null : menuOpen ? (
                  <ArrowUpIcon className={classesAccount.mainNavLinkArrow} />
                ) : (
                  <ArrowDownIcon className={classesAccount.mainNavLinkArrow} />
                )}
              </a>
            ) : (
              <a
                href="/profile"
                onClick={handleClick}
                className={classesAccount.mainNavLink}
                aria-expanded={menuOpen}
                aria-haspopup="true"
                aria-controls={id}
                role="button"
              >
                {userIsLoggedIn ? (
                  <>
                    <FormattedMessage
                      id="HELLO_USER"
                      defaultMessage="Hello, {firstName}"
                      values={{
                        firstName: currentUser?.firstName,
                      }}
                    >
                      {(text) => (
                        <Typography
                          component="p"
                          className={classesAccount.userNameDisplay}
                        >
                          {text}
                        </Typography>
                      )}
                    </FormattedMessage>
                  </>
                ) : (
                  <FormattedMessage id="LOGIN" defaultMessage="Login" />
                )}

                {menuOpen ? (
                  <ArrowUpIcon className={classesAccount.mainNavLinkArrow} />
                ) : (
                  <ArrowDownIcon className={classesAccount.mainNavLinkArrow} />
                )}
              </a>
            )}
          </div>
        </div>

        {menuOpen && (
          <ClickAwayListener onClickAway={() => handleClose()}>
            <div
              className={classesAccount.popoverContent}
              id={id}
              aria-hidden={!menuOpen}
            >
              {userIsLoggedIn ? (
                <div>
                  <Typography
                    className={classesAccount.accountWelcome}
                    component="p"
                    variant="body2"
                    data-scrub
                  >
                    {!currentUser || currentUser.__typename !== 'LoggedInUser'
                      ? formatMessage({
                          id: 'WELCOME_MESSAGE',
                          defaultMessage: 'Welcome ',
                        })
                      : `${formatMessage({
                          id: 'WELCOME_MESSAGE',
                          defaultMessage: 'Welcome ',
                        })} ${currentUser.firstName}`}
                  </Typography>
                  <Divider
                    variant="fullWidth"
                    className={classesAccount.popoverLoggedInDivider}
                  />
                  {}
                  <Link href={profileRoutes.index()}>
                    <a
                      className={classesAccount.popoverLink}
                      onClick={() =>
                        handleClose(MenuOption.Profile, profileRoutes.index())
                      }
                      data-testid="header-your-profile-link"
                      id="header-your-profile-link"
                    >
                      <FormattedMessage
                        id="YOUR_PROFILE"
                        defaultMessage="Profile"
                      />
                    </a>
                  </Link>
                  <Divider
                    className={classesAccount.popoverLoggedInDivider}
                    variant="fullWidth"
                  />
                  <Link href={orderCenterRoutes.dashboard()}>
                    <a
                      className={classesAccount.popoverLink}
                      onClick={() =>
                        handleClose(
                          MenuOption.Account,
                          orderCenterRoutes.dashboard()
                        )
                      }
                    >
                      <FormattedMessage
                        id="PRIMARY_NAV_ACCOUNT"
                        defaultMessage="Account"
                      />
                    </a>
                  </Link>
                  <Link
                    href={
                      !isOrderStatusCustomer ||
                      isExternalSalesAgent ||
                      isCustomerServiceRep
                        ? orderCenterRoutes.orders()
                        : orderCenterRoutes.orderSearch()
                    }
                  >
                    <a
                      className={classesAccount.popoverLink}
                      onClick={() =>
                        handleClose(
                          MenuOption.Orders,
                          !isOrderStatusCustomer ||
                            isExternalSalesAgent ||
                            isCustomerServiceRep
                            ? orderCenterRoutes.orders()
                            : orderCenterRoutes.orderSearch()
                        )
                      }
                      data-testid="header-your-orders-link"
                      id="header-your-orders-link"
                    >
                      <FormattedMessage
                        id="YOUR_ORDERS"
                        defaultMessage="Orders"
                      />
                    </a>
                  </Link>
                  {currentUser?.metadata?.isInvoiceCenterEnabled && (
                    <Link href={orderCenterRoutes.invoiceList()}>
                      <a
                        className={classesAccount.popoverLink}
                        onClick={() =>
                          handleClose(
                            MenuOption.Invoices,
                            orderCenterRoutes.invoiceList()
                          )
                        }
                        data-testid="header-invoices-link"
                        id="header-invoices-link"
                      >
                        <FormattedMessage
                          id="INVOICES"
                          defaultMessage="Invoices"
                        />
                      </a>
                    </Link>
                  )}
                  <Link href={orderCenterRoutes.buyItAgain()}>
                    <a
                      className={classesAccount.popoverLink}
                      onClick={() =>
                        handleClose(
                          MenuOption.BuyItAgain,
                          orderCenterRoutes.buyItAgain()
                        )
                      }
                      data-testid="header-buy-it-again-link"
                      id="header-buy-it-again-link"
                    >
                      <FormattedMessage
                        id="BUY_IT_AGAIN"
                        defaultMessage="Buy It Again"
                      />
                    </a>
                  </Link>
                  <Link href={orderCenterRoutes.savedCarts()}>
                    <a
                      className={classesAccount.popoverLink}
                      onClick={() =>
                        handleClose(
                          MenuOption.SavedCarts,
                          orderCenterRoutes.savedCarts()
                        )
                      }
                      data-testid="header-your-saved-carts-link"
                      id="header-your-saved-carts-link"
                    >
                      <FormattedMessage
                        id="SAVED_CARTS"
                        defaultMessage="Saved Carts"
                      />
                    </a>
                  </Link>

                  {canAccessQuotes && (
                    <Link href={orderCenterRoutes.quotes()}>
                      <a
                        className={classesAccount.popoverLink}
                        onClick={() =>
                          handleClose(
                            MenuOption.Quotes,
                            orderCenterRoutes.quotes()
                          )
                        }
                        data-testid="header-your-quotes-link"
                        id="header-your-quotes-link"
                      >
                        <FormattedMessage id="QUOTES" defaultMessage="Quotes" />
                      </a>
                    </Link>
                  )}

                  <Link href={orderCenterRoutes.lists()}>
                    <a
                      className={clsx(classesAccount.popoverLink)}
                      onClick={() =>
                        handleClose(MenuOption.Lists, orderCenterRoutes.lists())
                      }
                      data-testid="header-lists-link"
                      id="header-lists-link"
                    >
                      <FormattedMessage id="LISTS" defaultMessage="Lists" />
                    </a>
                  </Link>
                  <Divider
                    className={classesAccount.popoverLoggedInDivider}
                    variant="fullWidth"
                  />
                  {isInvoicePaymentCustomer && (
                    <div ref={step1InvoiceAnchorEl}>
                      <Link href={orderCenterRoutes.invoices()}>
                        <a
                          className={classesAccount.popoverLink}
                          onClick={() => {
                            handleClose(
                              MenuOption.PayInvoices,
                              orderCenterRoutes.invoices()
                            )
                            advanceInvoiceStepNumber(2)
                          }}
                          data-testid="header-invoices-link"
                          id="header-invoices-link"
                        >
                          <FormattedMessage
                            id="PAY_INVOICES"
                            defaultMessage="Pay Invoices"
                          />
                          <NewTag />
                        </a>
                      </Link>
                      {stepNumber === 1 && (
                        <InvoicesWalkMeStep
                          anchorEl={step1InvoiceAnchorEl}
                          placement="left"
                          stepNumber={1}
                        />
                      )}
                    </div>
                  )}
                  <Link href={subscriptionsRoutes.index()}>
                    <a
                      className={classesAccount.popoverLink}
                      onClick={() =>
                        handleClose(
                          MenuOption.Subscriptions,
                          subscriptionsRoutes.index()
                        )
                      }
                      data-testid="header-your-subscriptions-link"
                      id="header-your-subscriptions-link"
                    >
                      <FormattedMessage
                        id="EMPROVE_PROGRAM"
                        defaultMessage="Emprove® Program"
                      />
                    </a>
                  </Link>
                  <Link href={documentLibraryRoute.index()}>
                    <a
                      className={classesAccount.popoverLink}
                      onClick={() =>
                        handleClose(
                          MenuOption.DocumentLibrary,
                          documentLibraryRoute.index()
                        )
                      }
                      data-testid="header-document-library-link"
                      id="header-document-library-link"
                    >
                      <FormattedMessage
                        id="DOCUMENT_LIBRARY"
                        defaultMessage="Document Library"
                      />
                    </a>
                  </Link>
                  {isDigitalProductEnabled && (
                    <Link href={softwareLibraryRoutes.index()}>
                      <a
                        className={classesAccount.popoverLink}
                        onClick={() =>
                          handleClose(
                            MenuOption.SoftwareLibrary,
                            softwareLibraryRoutes.index()
                          )
                        }
                        data-testid="header-software-library-link"
                        id="header-software-library-link"
                      >
                        <FormattedMessage
                          id="SOFTWARE_LIBRARY"
                          defaultMessage="Software Library"
                        />
                      </a>
                    </Link>
                  )}
                  {isB2BUser && (
                    <Link href={aldrichMarketSelectRoute.index()}>
                      <a
                        className={classesAccount.popoverLink}
                        onClick={() =>
                          handleClose(
                            formatMessage({
                              id: 'ALDRICH_MARKET_SELECT',
                              defaultMessage: 'Aldrich® Market Select',
                            }),
                            aldrichMarketSelectRoute.index()
                          )
                        }
                        data-testid="header-aldrich-market-select-link"
                        id="header-aldrich-market-select-link"
                      >
                        <FormattedMessage
                          id="ALDRICH_MARKET_SELECT"
                          defaultMessage="Aldrich® Market Select"
                        />
                      </a>
                    </Link>
                  )}
                  {}
                  {isSourcingUser && logonId && (
                    <a
                      href={`/SourcingService/processInquiry.do?logonid=${logonId}`}
                      className={classesAccount.popoverLink}
                      onClick={() =>
                        handleClose(
                          MenuOption.ProductSourcing,
                          `/SourcingService/processInquiry.do?logonid=${logonId}`
                        )
                      }
                      rel="noopener noreferrer nofollow"
                      target="_blank"
                    >
                      <FormattedMessage
                        id="PRODUCT_SOURCING"
                        defaultMessage="Product Sourcing"
                      />
                    </a>
                  )}
                  {isInventoryViewUser && (
                    <a
                      href={'#'}
                      className={classesAccount.popoverLink}
                      onClick={() => {
                        setInventorySearchOpen(true)
                        handleClose()
                      }}
                      rel="noopener noreferrer nofollow"
                    >
                      <FormattedMessage
                        id="INVENTORY_BATCH_SEARCH"
                        defaultMessage="Inventory Batch Search"
                      />
                    </a>
                  )}
                  <Divider
                    className={classesAccount.popoverLoggedInDivider}
                    variant="fullWidth"
                  />

                  {}
                  {isSwitchAccountsEnabled &&
                  hasUniqueSoldTos &&
                  switchAccountOptionVisible ? (
                    <a
                      className={classesAccount.popoverLink}
                      onClick={() => {
                        setOpenSwitchAccount(true)
                        switchAccountTracking('initiate', 'account menu')
                      }}
                      rel="noopener noreferrer nofollow"
                    >
                      <FormattedMessage
                        id="SWITCH_ACCOUNTS"
                        defaultMessage="Switch Accounts"
                      />
                    </a>
                  ) : null}
                  {shouldShowInviteCoworker && (
                    <div className={classesAccount.coworks}>
                      {stepNumber === 2 && showWalkMeAlert && (
                        <>
                          <InviteCoWorkerWalkMeStep
                            anchorEl={step2AnchorEl}
                            placement="left"
                            stepNumber={stepNumber}
                            clickNext={(arg) => {
                              setStepNumber(arg)
                              arg === 2 && setMenuOpen(true)
                            }}
                          />
                          <Typography ref={step2AnchorEl} variant="inherit">
                            {' '}
                          </Typography>
                        </>
                      )}

                      <Link href={InviteCoWorkerRoute.index()}>
                        <a
                          className={clsx(classesAccount.popoverLink)}
                          data-testid="header-invite-co-worker-link"
                          id="header-invite-co-worker-link"
                          onClick={() => {
                            sendHeaderAccountMenuEvents(
                              'Invite a Coworker',
                              InviteCoWorkerRoute.index(),
                              true
                            )
                            setAlertBarReturnUrl()
                          }}
                        >
                          <FormattedMessage
                            defaultMessage="Invite a Coworker"
                            id="INVITE_COWORKER"
                          />
                          <NewTag />
                        </a>
                      </Link>
                    </div>
                  )}
                  <a
                    href="/logout"
                    className={classesAccount.popoverLink}
                    onClick={handleLogout}
                    data-testid="header-logout-link"
                    id="header-logout-link"
                  >
                    <FormattedMessage id="SIGN_OUT" defaultMessage="Sign Out" />
                  </a>
                </div>
              ) : (
                <Box
                  className={classesAccount.popoverLoginRegister}
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  gridRowGap="0.4rem"
                >
                  <LoginPopover>
                    <span
                      data-testid="header-login-link"
                      className={classesAccount.loginLinkWrapper}
                    >
                      <Link
                        href={`${loginRoute.index()}?redirect=${encodeURIComponent(
                          router.asPath || homeRoute.index()
                        )}`}
                      >
                        <Button
                          className={classesAccount.signInButton}
                          data-testid="header-login-link"
                          id="header-login-link"
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={() =>
                            sendHeaderMenuEvents({
                              menu: { id: 'LOGIN', defaultMessage: 'Login' },
                              navMenu: 'account',
                              navTreeOne: 'sign in',
                            })
                          }
                        >
                          <FormattedMessage
                            id="SIGN_IN"
                            defaultMessage="Sign In"
                          />
                        </Button>
                      </Link>
                    </span>
                  </LoginPopover>

                  <Divider
                    className={classesAccount.popoverDivider}
                    variant="fullWidth"
                  />
                  <Typography
                    className={classesAccount.popoverRegisterText}
                    component="p"
                    variant="body2"
                  >
                    <FormattedMessage
                      id="DO_NOT_HAVE_AN_ACCOUNT"
                      defaultMessage="Don't Have An Account?"
                    />
                  </Typography>
                  <Link href={registerRoute.index()}>
                    <Button
                      className={classesAccount.registerButton}
                      data-testid="header-registration-link"
                      id="header-registration-link"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onClick={() =>
                        sendHeaderMenuEvents({
                          menu: { id: 'SIGN_UP', defaultMessage: 'Sign Up' },
                          navMenu: 'login',
                          navTreeOne: 'register',
                        })
                      }
                    >
                      <FormattedMessage
                        id="REGISTER_TITLE"
                        defaultMessage="Register"
                      />
                    </Button>
                  </Link>
                </Box>
              )}
            </div>
          </ClickAwayListener>
        )}
      </div>
      {userIsLoggedIn && (
        <>
          {isInventoryViewUser && inventorySearchOpen && (
            <InventorySearchModal setInventorySearch={setInventorySearchOpen} />
          )}
        </>
      )}

      {
        <>
          {openSwitchAccount && (
            <SwitchAccountModal setSwitchAccountOpen={setOpenSwitchAccount} />
          )}
        </>
      }
      {
        <>
          {userIsLoggedIn &&
            !isB2BUser &&
            !hideVerifyEmailModal &&
            !isEmailVerified &&
            !router.pathname.includes('/email-verification') && (
              <EmailVerificationModal />
            )}
        </>
      }
    </>
  )
}

export default AccountMenu
